/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { Observable, concat, of } from 'rxjs';
import { catchError, map, toArray } from 'rxjs/operators';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { BulkResult, TrashRow, TrashService } from '../trash/trash.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const PAGE_SIZES = [20, 50, 100];

const BADGES: Record<string, string> = {
  pdf: 'PDF',
  doc: 'W',
  docx: 'W',
  xls: 'X',
  xlsx: 'X',
  ppt: 'P',
  pptx: 'P',
  txt: 'TXT',
  jpg: 'IMG',
  jpeg: 'IMG',
  png: 'IMG'
};

/**
 * Trash (UI-10).
 *
 * Lists deleted nodes from Alfresco's Trashcan API and offers restore and
 * permanent deletion, singly or in bulk. Permanent deletion always asks for
 * confirmation first, and bulk operations report per-item outcomes rather than
 * a single success or failure for the whole batch.
 */
@Component({
  selector: 'app-trash-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './trash-page.component.html',
  styleUrls: ['./trash-page.component.scss']
})
export class TrashPageComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly trash = inject(TrashService);
  private readonly notification = inject(NotificationService);
  private readonly dialog = inject(MatDialog);

  readonly pageSizes = PAGE_SIZES;

  readonly rows = signal<TrashRow[]>([]);
  readonly total = signal(0);
  readonly loading = signal(true);
  readonly working = signal(false);
  readonly failed = signal('');
  readonly noticeOpen = signal(true);
  readonly selectedIds = signal<Set<string>>(new Set());

  pageSize = PAGE_SIZES[0];
  pageIndex = 0;

  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly allSelected = computed(() => {
    const rows = this.rows();
    return rows.length > 0 && rows.every((row) => this.selectedIds().has(row.id));
  });
  readonly someSelected = computed(() => this.selectedCount() > 0 && !this.allSelected());

  ngOnInit(): void {
    this.reload();
  }

  // ---- data ---------------------------------------------------------------

  reload(): void {
    this.loading.set(true);
    this.failed.set('');

    this.trash.page(this.pageIndex * this.pageSize, this.pageSize).subscribe({
      next: ({ rows, total }) => {
        this.rows.set(rows);
        this.total.set(total);
        this.loading.set(false);
        // a selection that no longer exists on this page would be invisible
        this.selectedIds.update((ids) => new Set([...ids].filter((id) => rows.some((row) => row.id === id))));
      },
      error: (err) => {
        this.loading.set(false);
        this.rows.set([]);
        this.failed.set(this.describe(err, 'DMS.UI.7895D9'));
      }
    });
  }

  dismissNotice(): void {
    this.noticeOpen.set(false);
  }

  // ---- selection ----------------------------------------------------------

  isSelected(row: TrashRow): boolean {
    return this.selectedIds().has(row.id);
  }

  toggle(row: TrashRow): void {
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      return next;
    });
  }

  toggleAll(): void {
    const rows = this.rows();
    this.selectedIds.set(this.allSelected() ? new Set() : new Set(rows.map((row) => row.id)));
  }

  private selectedRows(): TrashRow[] {
    const ids = this.selectedIds();
    return this.rows().filter((row) => ids.has(row.id));
  }

  // ---- actions ------------------------------------------------------------

  restore(row: TrashRow): void {
    this.runBulk([row], (id) => this.trash.restore(id), 'DMS.UI.5B8DE4');
  }

  restoreSelected(): void {
    this.runBulk(this.selectedRows(), (id) => this.trash.restore(id), 'DMS.UI.5B8DE4');
  }

  purge(row: TrashRow): void {
    this.confirmPurge([row]);
  }

  purgeSelected(): void {
    this.confirmPurge(this.selectedRows());
  }

  /**
   * Empties the trash: every deleted node across all pages, not just the
   * selection. Confirmed first — this is irreversible for the whole list.
   * The ids are collected before the purge starts because deleting rows moves
   * the pages under a live `skipCount` walk.
   */
  purgeAll(): void {
    if (!this.total() || this.working()) {
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'DMS.UI.E54301',
          message: this.translate.instant('DMS.UI.1A255A', { p0: this.total() }),
          yesLabel: 'DMS.UI.092734',
          noLabel: 'DMS.UI.7D3094'
        },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed !== true) {
          return;
        }
        this.loading.set(true);
        this.trash.allRows().subscribe({
          next: (rows) => {
            this.loading.set(false);
            this.runBulk(rows, (id) => this.trash.purge(id), 'DMS.UI.092734');
          },
          error: (err) => {
            this.loading.set(false);
            this.notification.showError(this.describe(err, 'DMS.UI.7895D9'));
          }
        });
      });
  }

  /** Permanent deletion is irreversible, so it is always confirmed first. */
  private confirmPurge(rows: TrashRow[]): void {
    if (!rows.length) {
      return;
    }

    const message =
      rows.length === 1
        ? this.translate.instant('DMS.UI.2475C9', { p0: rows[0].name })
        : this.translate.instant('DMS.UI.1A255A', { p0: rows.length });

    this.dialog
      .open(ConfirmDialogComponent, {
        data: { title: 'DMS.UI.E54301', message, yesLabel: 'DMS.UI.092734', noLabel: 'DMS.UI.7D3094' },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed === true) {
          this.runBulk(rows, (id) => this.trash.purge(id), 'DMS.UI.092734');
        }
      });
  }

  /**
   * Applies an operation to each row in turn and reports the outcome per item.
   *
   * Sequential rather than parallel: these are repository mutations, and a
   * partial failure is much easier to explain when the order is deterministic.
   */
  private runBulk(rows: TrashRow[], operation: (id: string) => Observable<any>, verb: string): void {
    if (!rows.length || this.working()) {
      return;
    }

    this.working.set(true);
    const verbLabel = this.translate.instant(verb);

    concat(
      ...rows.map((row) =>
        operation(row.id).pipe(
          map(() => ({ id: row.id, name: row.name, reason: '' })),
          catchError((err) =>
            of({ id: row.id, name: row.name, reason: this.describe(err, this.translate.instant('DMS.UI.3E081F', { p0: verbLabel })) })
          )
        )
      )
    )
      .pipe(toArray())
      .subscribe((results) => {
        this.working.set(false);

        const outcome: BulkResult = {
          ok: results.filter((r) => !r.reason).map((r) => r.id),
          failed: results.filter((r) => r.reason)
        };

        if (!outcome.failed.length) {
          this.notification.showInfo(this.translate.instant('DMS.UI.D62D6E', { p0: verbLabel, p1: outcome.ok.length }));
        } else if (!outcome.ok.length) {
          this.notification.showError(this.translate.instant('DMS.UI.A7DB0E', { p0: verbLabel, p1: outcome.failed[0].reason }));
        } else {
          this.notification.showWarning(this.translate.instant('DMS.UI.1E5F61', { p0: verbLabel, p1: outcome.ok.length, p2: outcome.failed.length }));
        }

        this.selectedIds.set(new Set());
        this.reload();
      });
  }

  // ---- paging -------------------------------------------------------------

  changePageSize(size: number): void {
    this.pageSize = Number(size);
    this.pageIndex = 0;
    this.reload();
  }

  goToPage(index: number): void {
    if (index < 0 || index >= this.pageCount || index === this.pageIndex) {
      return;
    }
    this.pageIndex = index;
    this.reload();
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total() / this.pageSize));
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.pageCount }, (_, i) => i).slice(0, 10);
  }

  get rangeStart(): number {
    return this.rows().length ? this.pageIndex * this.pageSize + 1 : 0;
  }

  get rangeEnd(): number {
    return this.pageIndex * this.pageSize + this.rows().length;
  }

  // ---- view helpers -------------------------------------------------------

  label(value: string): string {
    return value ? DmsMetadataService.label(value) : '';
  }

  badge(row: TrashRow): string {
    return row.isFolder ? '' : (BADGES[this.extension(row.name)] ?? 'FILE');
  }

  badgeClass(row: TrashRow): string {
    return 'aca-trash__badge--' + (row.isFolder ? 'folder' : this.extension(row.name));
  }

  ageText(row: TrashRow): string {
    return this.translate.instant(row.daysInTrash === 0 ? 'DMS.UI.0491EA' : 'DMS.UI.1BFE71', { p0: row.daysInTrash });
  }

  formatSize(bytes?: number): string {
    if (bytes === undefined || bytes === null) {
      return '—';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  }

  trackById(_: number, row: TrashRow): string {
    return row.id;
  }

  private extension(name: string): string {
    return name.split('.').pop()?.toLowerCase() ?? '';
  }

  private describe(error: any, fallback: string): string {
    return error?.error?.error?.briefSummary || error?.message || this.translate.instant(fallback);
  }
}
